import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { FIELD_LIMITS, parentRegistrationSchema, type ParentRegistration } from '@leangap/types'
import { submitParentRegistration } from '@/services/parent'
import Header from '@/components/public/Header'
import PublicRegistrationWrapper from '@/components/public/PublicRegistrationWrapper'
import { CHILDREN_SELECT_OPTIONS } from '@/constants/referral'
import { SUBMISSION_SUCCESS_PARAGRAPHS } from '@/constants/messages'
import { Input, Select, SelectItem, Button } from '@heroui/react'
import CountrySelect from '@/components/form/CountrySelect'
import PhoneInput from '@/components/form/PhoneInput'
import { HowHearAboutUsQuestion } from '@/components/HowHearAboutUsQuestion'
import BackButton from '@/components/public/BackButton'
import { ArrowRight } from 'lucide-react'
import NameInput from '@/components/form/NameInput'

export default function ParentsPage() {
	const [isSubmitted, setIsSubmitted] = useState(false)
	const [submitError, setSubmitError] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)

	const form = useForm<ParentRegistration>({
		resolver: zodResolver(parentRegistrationSchema),
		mode: 'onChange',
	})
	const {
		register,
		handleSubmit,
		watch,
		setValue,
		control,
		clearErrors,
		trigger,
		formState: { errors, isValid },
	} = form

	useEffect(() => {
		const main = watch('referralSource')
		if (!main) {
			clearErrors('referralSource')
			clearErrors('referralSourceExtra')
		}
	}, [clearErrors, watch])

	const setCountry = (val: string) =>
		setValue('country' as const, val, { shouldValidate: true, shouldDirty: true })

	const onSubmit = async (data: ParentRegistration) => {
		setSubmitError('')
		setIsSubmitting(true)
		try {
			await submitParentRegistration(data)
			setIsSubmitted(true)
		} catch (e: unknown) {
			setSubmitError(e instanceof Error ? e.message : 'Submission failed')
		} finally {
			setIsSubmitting(false)
		}
	}

	const formElement = (
		<form className="mx-auto mt-8 w-full max-w-lg space-y-4" onSubmit={handleSubmit(onSubmit)}>
			<div className="flex gap-3">
				<NameInput control={control} name="firstName" label="First Name" />
				<NameInput control={control} name="lastName" label="Last Name" />
			</div>
			<Input
				label="Email"
				isRequired
				type="email"
				maxLength={FIELD_LIMITS.email}
				{...register('email')}
				errorMessage={errors.email?.message}
				isInvalid={!!errors.email}
			/>

			<Controller
				control={control}
				name="phoneNumber"
				render={({ field, fieldState }) => (
					<PhoneInput
						label="Phone Number"
						isRequired
						value={field.value ?? ''}
						onValueChange={field.onChange}
						onBlur={field.onBlur}
						errorMessage={fieldState.error?.message}
						isInvalid={!!fieldState.error}
					/>
				)}
			/>

			<CountrySelect
				onCountryChange={setCountry}
				defaultValue={watch('country') ?? ''}
				errorMessage={errors.country?.message}
				isInvalid={!!errors.country}
				isRequired
			/>

			<div className="flex gap-3">
				<Input
					label="City of Residence"
					isRequired
					maxLength={FIELD_LIMITS.city}
					{...register('city')}
					errorMessage={errors.city?.message}
					isInvalid={!!errors.city}
				/>
				<Input
					label="Zip Code"
					isRequired
					maxLength={FIELD_LIMITS.zipCode}
					{...register('zipCode')}
					errorMessage={errors.zipCode?.message}
					isInvalid={!!errors.zipCode}
				/>
			</div>

			<Select
				label="How many children do you have?"
				isRequired
				selectedKeys={[watch('numberOfChildren') as string]}
				onSelectionChange={(keys) => {
					const key = Array.isArray(keys)
						? keys[0]
						: typeof keys === 'string'
							? keys
							: (keys?.currentKey ?? '')
					setValue('numberOfChildren', key, { shouldValidate: true, shouldDirty: true })
				}}
				errorMessage={errors.numberOfChildren?.message}
				isInvalid={!!errors.numberOfChildren}
			>
				{CHILDREN_SELECT_OPTIONS.map((opt) => (
					<SelectItem key={opt.value}>{opt.label}</SelectItem>
				))}
			</Select>

			<Input
				label="Graduation year of your youngest child"
				isRequired
				maxLength={4}
				{...register('youngestChildGradYear')}
				errorMessage={errors.youngestChildGradYear?.message}
				isInvalid={!!errors.youngestChildGradYear}
			/>

			<div className="mt-8">
				<HowHearAboutUsQuestion
					register={register}
					watch={watch}
					setValue={setValue}
					clearErrors={clearErrors}
					trigger={trigger}
					errors={errors}
					name="referralSource"
					extraName="referralSourceExtra"
				/>
			</div>

			<Input
				label="Is there something else you'd like to share with us?"
				maxLength={FIELD_LIMITS.longText}
				{...register('additionalComments')}
			/>

			{submitError && (
				<div className="text-danger bg-danger-50 mb-4 rounded-lg p-4">{submitError}</div>
			)}

			<Button
				color="primary"
				type="submit"
				className="mt-6 w-full"
				isDisabled={!isValid || isSubmitting}
				isLoading={isSubmitting}
				size="lg"
			>
				{isSubmitting ? 'Submitting...' : 'Submit'}{' '}
				{!isSubmitting && <ArrowRight className="size-5" />}
			</Button>
		</form>
	)

	return (
		<div className="min-h-vh flex flex-col items-center justify-center bg-black">
			<BackButton />
			<Header description={isSubmitted ? 'Welcome to Leangap!' : 'Welcome parent!'} />

			<PublicRegistrationWrapper
				descriptionParagraphs={[
					'Our application is for high school students aged 14 to 17. Your child must apply with their own email in a separate session.',
					'To help you decide if Leangap is right for your teen, we’ll send you program details and the application questions, invite you to info sessions, and offer a call with our managing director.',
				]}
				successParagraphs={SUBMISSION_SUCCESS_PARAGRAPHS}
				isSubmitted={isSubmitted}
			>
				{formElement}
			</PublicRegistrationWrapper>
		</div>
	)
}
